import { useQuery } from "@tanstack/react-query";
import { fetchRuns, fmtDate, fmtRelative } from "../lib/api";
import HowItWorks from "../components/HowItWorks";

const STATUS_DOT = {
  completed: "#10B981",
  partial: "#F59E0B",
  failed: "#EF4444",
  running: "#3B82F6",
  queued: "#71717A",
};

export default function RunsPage() {
  const { data: runs = [] } = useQuery({
    queryKey: ["runs-all"],
    queryFn: () => fetchRuns({ limit: 200 }),
    refetchInterval: 8000,
  });

  return (
    <div data-testid="runs-page" className="p-4 space-y-3">
      <div>
        <h1 className="font-mono text-2xl tracking-tight">DAILY_RUNS</h1>
        <p className="text-zinc-500 text-[12px] mt-1 max-w-2xl leading-relaxed">
          Every time the scheduler fills a library, generates an innovation
          item, or sweeps fun-facts, it logs a run here. Use this page to
          confirm the engine is alive and see where each batch came from —
          from request → generation → insertion → verified sources.
        </p>
      </div>

      <HowItWorks
        id="runs"
        tag="REAL_TIME · 8s_REFRESH"
        headline="Every row is one scheduler tick. Status + counters tell you how that batch went."
        steps={[
          { accent: "emerald", label: "STATUS color", body: "Green = completed, amber = partial (under quota), red = failed. Blue = currently running." },
          { accent: "amber", label: "REQ → GEN → INS", body: "REQ is how many items we asked the LLM for; GEN is what it produced; INS is what landed after dedup + the quality gate." },
          { accent: "purple", label: "DUP column", body: "Items rejected because their title hash already exists in the library." },
          { accent: "sky", label: "SRC_V/A", body: "Source URLs verified live vs total attached. Higher is better — we tier by publisher credibility." },
          { accent: "rose", label: "TRIGGER", body: "Who fired the run: 'scheduler-*' is the engine; 'user-*' or 'owner-*' = manual." },
        ]}
      />

      <section className="border border-[#27272A] bg-[#0A0A0A] overflow-x-auto">
        <table className="w-full text-[11px] font-mono min-w-[1100px]">
          <thead className="text-zinc-500 bg-[#0E0E0E] sticky top-0">
            <tr className="border-b border-[#27272A]">
              <th className="text-left p-2 uppercase tracking-wider">DAY</th>
              <th className="text-left p-2 uppercase tracking-wider">STARTED</th>
              <th className="text-left p-2 uppercase tracking-wider">TOPIC</th>
              <th className="text-left p-2 uppercase tracking-wider">STATUS</th>
              <th className="text-right p-2 uppercase tracking-wider">REQ</th>
              <th className="text-right p-2 uppercase tracking-wider">GEN</th>
              <th className="text-right p-2 uppercase tracking-wider">INS</th>
              <th className="text-right p-2 uppercase tracking-wider">DUP</th>
              <th className="text-right p-2 uppercase tracking-wider">SRC_V/A</th>
              <th className="text-right p-2 uppercase tracking-wider">DUR_MS</th>
              <th className="text-left p-2 uppercase tracking-wider">TRIGGER</th>
              <th className="text-left p-2 uppercase tracking-wider">ERROR</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr
                key={r.id}
                data-testid={`run-row-${r.id}`}
                className="border-b border-[#161616] hover:bg-[#0E0E0E]"
              >
                <td className="p-2 text-zinc-400">{r.day_key}</td>
                <td className="p-2 text-zinc-400" title={fmtDate(r.started_at)}>
                  {fmtRelative(r.started_at)}
                </td>
                <td className="p-2 uppercase">
                  {r.topic_slug || (
                    <span className="text-zinc-500">ALL</span>
                  )}
                </td>
                <td className="p-2 uppercase">
                  <span
                    className="dot"
                    style={{ background: STATUS_DOT[r.status] || "#71717A" }}
                  />
                  {r.status}
                </td>
                <td className="p-2 text-right tabnum">{r.ideas_requested}</td>
                <td className="p-2 text-right tabnum">{r.ideas_generated}</td>
                <td className="p-2 text-right tabnum text-emerald-400">
                  {r.ideas_inserted}
                </td>
                <td className="p-2 text-right tabnum text-amber-400">
                  {r.ideas_deduped}
                </td>
                <td className="p-2 text-right tabnum">
                  {r.sources_verified}/{r.sources_attached}
                </td>
                <td className="p-2 text-right tabnum">{r.duration_ms}</td>
                <td className="p-2 uppercase text-zinc-500">{r.triggered_by}</td>
                <td
                  className="p-2 text-rose-400 max-w-[260px] truncate"
                  title={r.error || ""}
                >
                  {r.error || ""}
                </td>
              </tr>
            ))}
            {runs.length === 0 && (
              <tr>
                <td colSpan={12} className="p-6 text-center text-zinc-500">
                  NO_RUNS_RECORDED
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
